import { z } from 'zod';
import { TranslationProvider } from './models.js';
// --- Schema Definitions based on Authoritative TypeScript Types ---
// Based on: packages/types/src/document/DocumentIdentity.ts -> DocumentIdentifier
const DocumentIdentifierSchema = z.object({
    id: z.number(),
    type: z.enum(['text', 'pdf']),
});
// Based on: packages/types/src/text/paragraph.ts -> ParagraphData
const ParagraphDataSchema = z.object({
    id: z.string(),
    content: z.string(),
    index: z.number(),
    type: z.string().optional(),
    metadata: z.record(z.unknown()).optional(),
});
// Based on: packages/types/src/content/selection.ts -> TextLocation
const TextLocationSchema = z.object({
    paragraphData: ParagraphDataSchema,
    startOffset: z.number(),
    endOffset: z.number(),
});
// Based on: packages/types/src/content/selection.ts -> TextSelection
const TextSelectionSchema = z.object({
    type: z.literal('text'),
    selectedText: z.string(),
    location: TextLocationSchema,
});
// Based on: packages/types/src/pdf/plumber.ts -> PdfPlumberWord['rect']
const PdfPlumberRectSchema = z.object({
    x0: z.number(),
    top: z.number(),
    x1: z.number(),
    bottom: z.number(),
});
// Based on: packages/types/src/pdf/layout.ts -> PreRenderedWord
const PreRenderedWordSchema = z.object({
    id: z.string(),
    text: z.string(),
    rect: PdfPlumberRectSchema,
    height: z.number(),
});
// Based on: packages/types/src/pdf/layout.ts -> PreRenderedParagraph
const PreRenderedParagraphSchema = z.object({
    paragraphId: z.string(),
    words: z.array(PreRenderedWordSchema),
    index: z.number(),
    pageNumber: z.number(),
});
// Based on: packages/types/src/content/selection.ts -> SerializableRect
const SerializableRectSchema = z.object({
    x: z.number(),
    y: z.number(),
    width: z.number(),
    height: z.number(),
    top: z.number(),
    left: z.number(),
    right: z.number(),
    bottom: z.number(),
});
// Based on: packages/types/src/content/selection.ts -> PdfLocation
const PdfLocationSchema = z.object({
    paragraphId: z.string(),
    paragraphData: PreRenderedParagraphSchema.optional(),
    startWordId: z.string(),
    endWordId: z.string(),
    startWordOffset: z.number(),
    endWordOffset: z.number(),
    selectionRects: z.array(SerializableRectSchema),
});
// Based on: packages/types/src/content/selection.ts -> PdfSelection
const PdfSelectionSchema = z.object({
    type: z.literal('pdf'),
    selectedText: z.string(),
    location: PdfLocationSchema,
});
// Based on: packages/types/src/content/selection.ts -> SelectionData (discriminated union)
const SelectionDataSchema = z.discriminatedUnion('type', [TextSelectionSchema, PdfSelectionSchema]);
// Zod schema for TranslationRequest, fully aligned with TypeScript interfaces
export const TranslationRequestSchema = z.object({
    text: z.string().min(1, { message: 'Text to translate cannot be empty.' }),
    provider: z.nativeEnum(TranslationProvider),
    sourceLanguage: z.string().optional(),
    targetLanguage: z.string().optional(),
    model: z.string(),
    context: z.array(z.string()).optional(),
    analyze: z.boolean().optional(),
    options: z
        .object({
        beforeText: z.string().optional(),
        afterText: z.string().optional(),
        documentIdentity: DocumentIdentifierSchema.optional(),
        selection: SelectionDataSchema.optional(),
        bypassCache: z.boolean().optional(),
    })
        .passthrough() // Matches the [key: string]: unknown in the interface
        .optional(),
});
//# sourceMappingURL=translation.schemas.js.map